import * as React from 'react';

import { ReportOptions, ReportTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { PaywallScreens } from '~analytics/types';
import { Badge } from '~shared/components/Badge';
import { FieldRow } from '~shared/components/FieldRow';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import {
  PaywallTouch,
  usePaywallContext,
} from '~teacher/components/PaywallDialog';

import { updateOptionTo } from './ReportOptionHook';
import { ExportReportDispatch } from './ReportOptionHookReducer';

export interface Props {
  label: string;
  value: boolean;
  identifier: ReportOptions;
  dispatch: ExportReportDispatch;
  shouldProGuardReportOptionRow: boolean;
  shouldShowProBadge: boolean;
  reportType?: ReportTypes;
}

export const ReportOptionBooleanControl: React.FC<Props> = ({
  label,
  value,
  identifier,
  dispatch,
  shouldProGuardReportOptionRow,
  shouldShowProBadge,
  reportType,
}: React.PropsWithChildren<Props>) => {
  const { dispatch: paywallDispatch } = usePaywallContext();

  const ProBadge = React.useMemo(() => {
    if (shouldProGuardReportOptionRow && shouldShowProBadge) {
      return (
        <div className="sm:ml-2 mb-2 sm:mb-0">
          <Badge>PRO</Badge>
        </div>
      );
    }
    return null;
  }, [shouldProGuardReportOptionRow, shouldShowProBadge]);

  const handleChange = React.useCallback(
    (checked: boolean) => {
      if (shouldProGuardReportOptionRow) {
        paywallDispatch({
          type: 'OPEN',
          touch: getPaywallScreen(reportType, identifier),
        });
      } else {
        updateOptionTo(dispatch)({ identifier, value: checked });
      }
    },
    [
      shouldProGuardReportOptionRow,
      paywallDispatch,
      reportType,
      identifier,
      dispatch,
    ]
  );

  return (
    <FieldRow label={translate(label)} id={identifier} icon={ProBadge}>
      <ToggleSwitch
        id={identifier}
        labelledBy={`${identifier}-id`}
        on={value}
        autoFocus={value}
        onChange={(checked) => handleChange(checked)}
      />
    </FieldRow>
  );
};

function getPaywallScreen(type, identifier): PaywallTouch {
  switch (type) {
    case 'individual':
      switch (identifier) {
        case 'include_explanations':
          return [
            PaywallScreens.ReportOptionsIndividualStudentsIncludeExplanations,
          ];

        case 'include_correct_answers':
          return [
            PaywallScreens.ReportOptionsIndividualStudentsIncludeCorrectAnswers,
          ];
      }
      break;

    case 'whole':
      if (identifier === 'include_mc_answer_content') {
        return [
          PaywallScreens.ReportOptionsCompleteResultsIncludeMCAnswerContent,
        ];
      }
      break;

    case 'question':
      if (identifier === 'include_explanations') {
        return [PaywallScreens.ReportOptionsResultsSummaryIncludeExplanations];
      }
      break;

    case 'blank':
      if (identifier === 'include_explanations') {
        return [PaywallScreens.ReportOptionsAnswerKeyIncludeExplanations];
      }
      break;

    default:
      return [null];
  }
}
